import { computed, ref } from "vue";
import { useAuthStore, useCandidatesStore } from "@/stores";
import useSWRV from "swrv";
import useAwards from "./use-awards";

type Options = {
  refreshInterval?: number;
};

export default function useCandidate(
  candidateId: string = "",
  options: Options = { refreshInterval: 60000 }
) {
  const authStore = useAuthStore();
  const candidatesStore = useCandidatesStore();
  const { awardYear, instance: awardInstance } = useAwards();

  const computedCandidateId = computed(() => {
    return (
      authStore.candidateId ||
      candidateId ||
      candidatesStore.currentCandidate?.id
    );
  });

  const _candidateId = ref(candidateId || computedCandidateId);

  const { data: candidate, mutate: refreshCandidate } = useSWRV(
    () => _candidateId.value && `/candidate/${_candidateId.value}`,
    () => candidatesStore.getCandidate(_candidateId.value),
    { refreshInterval: options.refreshInterval }
  );

  function setCandidate(id: string) {
    _candidateId.value = id;
  }

  const instance = computed(() => {
    if (!candidate.value?.instances.length) {
      return null;
    }
    return (
      candidate.value.instances.find(
        (i) => parseInt(i.SK, 10) === awardYear.value
      ) ?? null
    );
  });

  const instances = computed(() => {
    if (!candidate.value?.instances.length) {
      return [];
    }
    return candidate.value.instances.sort((a, b) => {
      return parseInt(b.SK, 10) - parseInt(a.SK, 10);
    });
  });

  const status = computed(() => {
    const categories = candidate.value?.categories || [];
    const results = candidate.value?.results || [];
    return {
      isNominee: categories.some((c) =>
        c.finalizedYears?.includes(awardYear.value)
      ),
      isFinalist: categories.some(
        (c) => c.finalistsYears?.[awardYear.value]?.length > 0
      ),
      isWinner: categories.some((c) =>
        c.winnersYears?.includes(awardYear.value)
      ),
      isPreviousFinalist: results.some((result) => result.finalist),
      isPreviousWinner: results.some((result) => result.winner),
    };
  });

  const ceremonyNight = computed(() => {
    const { awardsCeremonyNights = [] } = awardInstance.value || {};
    if (!awardsCeremonyNights.length) {
      return null;
    }
    const categoryIds = awardsCeremonyNights.flatMap((n) => n.categoryIds);
    if (!categoryIds.length) {
      return null;
    }
    const finalistCategory = candidate.value?.categories.find(
      (c) => c.finalistsYears?.[awardYear.value]?.length > 0
    );
    if (!finalistCategory) {
      return null;
    }
    const night = awardsCeremonyNights.find((n) =>
      n.categoryIds.includes(finalistCategory.categoryId)
    );
    return night ?? null;
  });

  const ceremonyNightIndex = computed(() => {
    const { awardsCeremonyNights = [] } = awardInstance.value || {};
    if (!awardsCeremonyNights?.length) {
      // no nights defined, default to first night
      return 0;
    }
    const categoryIds = awardsCeremonyNights.flatMap((n) => n.categoryIds);
    if (!categoryIds.length) {
      // no categories specified for any night, default to first night
      return 0;
    }
    const finalistCategory = candidate.value?.categories?.find(
      (c) => c.finalistsYears?.[`${awardYear.value}`]?.length > 0
    );
    if (!finalistCategory) {
      return -1;
    }
    const night = awardsCeremonyNights.findIndex((n) =>
      n.categoryIds.includes(finalistCategory.categoryId)
    );
    return night;
  });

  const claimedNomination = computed(() => {
    return candidate.value?.claims.some((c) =>
      [awardYear.value, awardYear.value + 1].includes(c.awardYear)
    );
  });

  return {
    candidate,
    ceremonyNight,
    ceremonyNightIndex,
    claimedNomination,
    instance,
    instances,
    setCandidate,
    status,
    refresh: () => Promise.all([refreshCandidate()]),
  };
}
